import { useEffect, useId, useState } from 'react';
import { AddOutlined, DeleteOutlineRounded, DriveFileMoveOutlined, MoreHorizRounded, UndoRounded } from '@mui/icons-material';
import { Box, IconButton, ListItemIcon, ListItemText, MenuItem, Tooltip } from '@mui/material';
import { AnchoredMenu } from '../../../design-system';
import type { OrganizationPlanNode } from '../organizationPlan';

export function OrganizationStudioOrganizationMenu({ node, nodes, disabled, onMove, onCreateChild, onEnd, onUndoEnd }: {
  node: OrganizationPlanNode;
  nodes: readonly OrganizationPlanNode[];
  disabled: boolean;
  onMove: () => void;
  onCreateChild: () => void;
  onEnd: () => void;
  onUndoEnd: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const children = nodes.filter(child => child.parentId === node.id && child.pending !== 'END');
  const blocked = node.count > 0 || children.length > 0;
  const ended = node.pending === 'END';
  const endLabel = node.pending === 'CREATE' ? '신설 취소' : '조직 종료';
  const endDisabledReason = !node.parentId
    ? '최상위 조직은 종료할 수 없습니다.'
    : blocked
      ? `${[node.count > 0 ? `직속 ${node.count}명` : '', children.length > 0 ? `하위 조직 ${children.length}개` : ''].filter(Boolean).join(' · ')} 이동 후 종료할 수 있습니다.`
      : undefined;
  useEffect(() => { setAnchor(null); }, [node.id]);
  useEffect(() => { if (disabled) setAnchor(null); }, [disabled]);
  const run = (action: () => void) => {
    if (disabled) return;
    setAnchor(null);
    action();
  };
  return <>
    <Tooltip title="조직 작업">
      <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <IconButton size="small" aria-label="조직 작업" aria-haspopup="menu" aria-controls={anchor ? menuId : undefined}
          aria-expanded={Boolean(anchor)} disabled={disabled} onClick={event => setAnchor(event.currentTarget)}>
          <MoreHorizRounded fontSize="small" />
        </IconButton>
      </Box>
    </Tooltip>
    <AnchoredMenu id={menuId} anchorEl={anchor} open={Boolean(anchor && !disabled)} ariaLabel="조직 작업" placement="bottom-end" onClose={() => setAnchor(null)}>
      {ended && <MenuItem onClick={() => run(onUndoEnd)}>
        <ListItemIcon><UndoRounded /></ListItemIcon><ListItemText primary="종료 취소" />
      </MenuItem>}
      {!ended && node.parentId && <MenuItem onClick={() => run(onMove)}>
        <ListItemIcon><DriveFileMoveOutlined /></ListItemIcon><ListItemText primary="조직 이동" />
      </MenuItem>}
      {!ended && <MenuItem onClick={() => run(onCreateChild)}>
        <ListItemIcon><AddOutlined /></ListItemIcon><ListItemText primary="하위 조직 추가" />
      </MenuItem>}
      {!ended && <MenuItem aria-label={endLabel} aria-describedby={endDisabledReason ? `${menuId}-end-reason` : undefined} disabled={!node.parentId || blocked} onClick={() => {
        if (!node.parentId || blocked) return;
        run(onEnd);
      }} sx={{ color: 'error.main' }}>
        <ListItemIcon sx={{ color: 'inherit' }}><DeleteOutlineRounded /></ListItemIcon>
        <ListItemText primary={endLabel} secondary={endDisabledReason} slotProps={{ secondary: { id: `${menuId}-end-reason`, sx: { whiteSpace: 'normal' } } }} />
      </MenuItem>}
    </AnchoredMenu>
  </>;
}
